import OrganizationSessionPolicy from '../components/OrganizationSessionPolicy';
import OrganizationSsoSettings from '../components/OrganizationSsoSettings';
import OrganizationMfaPolicy from '../components/OrganizationMfaPolicy';
import React, { useState, useEffect, useCallback } from 'react';
import { isAdminOrPlatform, isTenantAdmin } from '../lib/access';
import { axiosInstance } from '../App';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Settings,
  Shield,
  Bell,
  Link2,
  RefreshCw,
  Download,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  User,
  FileText,
  AlertTriangle,
  ExternalLink,
  Loader2,
  Eye,
  EyeOff,
  Plug,
  Mail,
  MessageSquare,
  Users,
  Webhook,
  BellRing,
  BellOff,
  Activity,
  Calendar,
  LogIn,
  ClipboardList,
  Check,
  MessageCircle,
  Globe2,
  SlidersHorizontal,
  LayoutDashboard,
  Star,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import WebModeSettings from '../components/WebModeSettings';
import IntegrationAccessManager from '../components/IntegrationAccessManager';
import PluginsPanel from '../components/PluginsPanel';
import TeamsSetupDialog from '../components/TeamsSetupDialog';
import KnowledgeUploadSettings from '../components/KnowledgeUploadSettings';
import GeneralSettingsPanel from '../components/GeneralSettingsPanel';
import InfoTip from '../components/InfoTip';
import TenantAccessControls from '../components/TenantAccessControls';
import OrganisationOverview, { LeaderList, useOrganisationOverview } from '../components/org/OrganisationOverview';
import DomainSettings from '../components/org/DomainSettings';
import AbilitiesSettings from '../components/org/AbilitiesSettings';
import { PluginsKnowledgeSources } from '../components/sources/KnowledgeSources';

// Admin, for the Tenant Admin; Members allowed to manage Web Mode only see the Knowledge sections.
const LeadershipSection = () => {
  const navigate = useNavigate();
  const { overview, error } = useOrganisationOverview();
  if (error) return <Card className="p-6 text-sm text-rose-700">{error}</Card>;
  if (!overview) return <Card className="p-6 text-sm text-slate-500">Loading…</Card>;
  return <div className="space-y-4" data-testid="organisation-leadership">
    <Card className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm text-slate-600"><span>Floor Leaders answer and steer Floor Space; Source Leaders look after knowledge. Each can cover the whole organisation or specific teams. Assign them per person in Teams &amp; Users.</span><Button variant="outline" onClick={() => navigate('/team-users')}>Assign in Teams &amp; Users</Button></Card>
    <div className="grid gap-4 lg:grid-cols-2"><LeaderList title="Floor Leaders" icon={MessageCircle} leaders={overview.leaders.floor} empty="No Floor Leaders yet." /><LeaderList title="Source Leaders" icon={Shield} leaders={overview.leaders.source} empty="No Source Leaders yet." /></div>
  </div>;
};

const System = ({ user, setUser, integrationsOnly = false }) => {
  const [searchParams] = useSearchParams();
  const canManageQflow = user?.role === 'admin' || user?.role === 'master_admin' || user?.is_master_admin === true;
  const canManageAppearance = isAdminOrPlatform(user);
  const canManageWebMode = canManageQflow || user?.permissions?.can_manage_web_mode === true;
  const canManageSystemOperations = canManageAppearance;
  // Ticks are set by the organisation's own Tenant Admin.
  const canEditAbilities = user?.role === 'admin' && user?.is_master_admin !== true;
  const systemDefaultTab = canManageAppearance ? 'overview' : canManageWebMode ? 'web-mode' : 'audit';
  const requestedSection = searchParams.get('section');
  // Coming back from a cloud sign-in lands on Plugins → Knowledge sources.
  useEffect(() => {
    if (!integrationsOnly) return;
    const connector = searchParams.get('connector');
    const outcome = searchParams.get('status');
    if (connector && outcome === 'connected') toast.success('Connected. Open the source to choose folders and files.');
    else if (connector && outcome === 'error') toast.error(searchParams.get('message') || 'The connection was not completed.');
    if (connector || window.location.hash === '#knowledge-sources') {
      setTimeout(() => document.getElementById('knowledge-sources')?.scrollIntoView({ behavior: 'smooth' }), 400);
    }
  }, [integrationsOnly, searchParams]);
  // Links such as the Learn Qflow walkthroughs can switch section while this page is open.
  useEffect(() => {
    if (requestedSection && (canManageAppearance || ['web-mode', 'knowledge-rules'].includes(requestedSection))) setActiveTab(requestedSection);
  }, [requestedSection, canManageAppearance]);
  const [activeTab, setActiveTab] = useState(integrationsOnly ? 'integrations' : (canManageAppearance || requestedSection === 'web-mode' || requestedSection === 'knowledge-rules') && requestedSection ? requestedSection : systemDefaultTab);
  // React Router can reuse this component while moving between /system and
  // /integrations. Derive the visible tab from the route prop so a previously
  // selected System Settings panel can never leak into Plugins.
  const visibleTab = integrationsOnly
    ? 'integrations'
    : activeTab === 'integrations' ? systemDefaultTab : activeTab;
  const [loading, setLoading] = useState(true);
  
  // Audit Logs State
  const [auditLogs, setAuditLogs] = useState({ logs: [], total: 0, filters: {} });
  const [auditFilters, setAuditFilters] = useState({
    user_id: '',
    action: '',
    entity_type: '',
    start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [auditPage, setAuditPage] = useState(0);
  
  // Integrations State
  const [integrations, setIntegrations] = useState([]);
  const [integrationTemplates, setIntegrationTemplates] = useState({});
  const [integrationCapabilities, setIntegrationCapabilities] = useState({});
  const [alertEvents, setAlertEvents] = useState([]);
  const [teamsSetupFor, setTeamsSetupFor] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrationDialog, setIntegrationDialog] = useState({ open: false, mode: 'create', data: null });
  const [showSecrets, setShowSecrets] = useState({});
  
  // Notifications State
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [notificationPrefs, setNotificationPrefs] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [floorSettings, setFloorSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loginEvents, setLoginEvents] = useState([]);
  const [loginFilters, setLoginFilters] = useState({ user_id: 'all', successful: 'all', login_from: '', login_to: '', limit: '100' });
  const [tasks, setTasks] = useState([]);
  const [taskFilters, setTaskFilters] = useState({ status: 'all', assignee_id: 'all', priority: 'all' });
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', assignee_id: 'none', priority: 'medium', due_date: '', status: 'open' });

  const fetchAuditLogs = useCallback(async () => {
    try {
      const params = {
        limit: 50,
        offset: auditPage * 50,
        ...Object.fromEntries(Object.entries(auditFilters).filter(([_, v]) => v)),
      };
      const res = await axiosInstance.get('/admin/audit-logs', { params });
      setAuditLogs(res.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  }, [auditFilters, auditPage]);

  const fetchIntegrations = useCallback(async () => {
    try {
      const [integrationsRes, templatesRes, capabilitiesRes, usersRes, alertEventsRes] = await Promise.all([
        axiosInstance.get('/admin/integrations'),
        axiosInstance.get('/admin/integrations/templates'),
        axiosInstance.get('/admin/integrations/capabilities'),
        axiosInstance.get('/users'),
        axiosInstance.get('/admin/integrations/alert-events').catch(() => ({ data: [] })),
      ]);
      setAlertEvents(alertEventsRes.data || []);
      setIntegrations(integrationsRes.data);
      setIntegrationTemplates(templatesRes.data);
      setIntegrationCapabilities(capabilitiesRes.data || {});
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, prefsRes, activityRes] = await Promise.all([
        axiosInstance.get('/admin/notifications'),
        axiosInstance.get('/admin/notifications/preferences'),
        axiosInstance.get('/admin/activity-feed'),
      ]);
      setNotifications(notifRes.data);
      setNotificationPrefs(prefsRes.data);
      setActivityFeed(activityRes.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  const fetchLoginEvents = useCallback(async () => {
    try {
      const params = { limit: Number(loginFilters.limit) };
      if (loginFilters.user_id !== 'all') params.user_id = loginFilters.user_id;
      if (loginFilters.successful !== 'all') params.successful = loginFilters.successful === 'success';
      if (loginFilters.login_from) params.login_from = loginFilters.login_from;
      if (loginFilters.login_to) params.login_to = loginFilters.login_to;
      const [eventsResponse, usersResponse] = await Promise.all([axiosInstance.get('/admin/login-events', { params }), axiosInstance.get('/users')]);
      setLoginEvents(eventsResponse.data || []);
      setUsers(usersResponse.data || []);
    } catch (error) { console.error('Failed to fetch login events:', error); }
  }, [loginFilters]);

  const fetchTasks = useCallback(async () => {
    try {
      const params = {};
      if (taskFilters.status !== 'all') params.task_status = taskFilters.status;
      if (taskFilters.assignee_id !== 'all') params.assignee_id = taskFilters.assignee_id;
      if (taskFilters.priority !== 'all') params.priority = taskFilters.priority;
      const [tasksResponse, usersResponse] = await Promise.all([axiosInstance.get('/admin/tasks', { params }), axiosInstance.get('/users')]);
      setTasks(tasksResponse.data || []);
      setUsers(usersResponse.data || []);
    } catch (error) { console.error('Failed to fetch tasks:', error); }
  }, [taskFilters]);

  const createTask = async (event) => {
    event.preventDefault();
    try {
      await axiosInstance.post('/admin/tasks', { ...taskForm, assignee_id: taskForm.assignee_id === 'none' ? null : taskForm.assignee_id, due_date: taskForm.due_date || null });
      toast.success('Task created'); setTaskDialogOpen(false);
      setTaskForm({ title: '', assignee_id: 'none', priority: 'medium', due_date: '', status: 'open' });
      fetchTasks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create task'); }
  };

  const updateTaskStatus = async (taskId, statusValue) => {
    try { await axiosInstance.patch(`/admin/tasks/${taskId}/status`, { status: statusValue }); fetchTasks(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to update task'); }
  };
  const fetchFloorSettings = useCallback(async () => {
    try { const response = await axiosInstance.get('/floor/settings'); setFloorSettings(response.data); }
    catch (error) { console.error('Failed to fetch Floor Space settings', error); }
  }, []);

  const saveFloorSettings = async () => {
    try { const response = await axiosInstance.put('/floor/settings', floorSettings); setFloorSettings(response.data); toast.success('Floor Space privacy settings saved'); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Failed to save Floor Space settings'); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    if (integrationsOnly) {
      await fetchIntegrations();
    } else if (canManageSystemOperations) {
      await Promise.all([fetchAuditLogs(), fetchNotifications(), fetchLoginEvents(), fetchTasks(), fetchFloorSettings()]);
    }
    setLoading(false);
  }, [integrationsOnly, canManageSystemOperations, fetchAuditLogs, fetchIntegrations, fetchNotifications, fetchLoginEvents, fetchTasks, fetchFloorSettings]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!integrationsOnly && canManageSystemOperations) fetchAuditLogs();
  }, [integrationsOnly, canManageSystemOperations, auditFilters, auditPage, fetchAuditLogs]);

  useEffect(() => { if (!integrationsOnly && canManageSystemOperations) fetchLoginEvents(); }, [integrationsOnly, canManageSystemOperations, fetchLoginEvents]);
  useEffect(() => { if (!integrationsOnly && canManageSystemOperations) fetchTasks(); }, [integrationsOnly, canManageSystemOperations, fetchTasks]);
  // Audit Log Functions
  const handleExportLogs = async (exportFormat) => {
    try {
      const params = {
        format: exportFormat,
        start_date: auditFilters.start_date,
        end_date: auditFilters.end_date,
      };
      
      if (exportFormat === 'csv') {
        const res = await axiosInstance.get('/admin/audit-logs/export', {
          params,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit_logs_${format(new Date(), 'yyyyMMdd')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Audit logs exported');
      } else {
        const res = await axiosInstance.get('/admin/audit-logs/export', { params });
        const dataStr = JSON.stringify(res.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit_logs_${format(new Date(), 'yyyyMMdd')}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Audit logs exported');
      }
    } catch (error) {
      toast.error('Failed to export logs');
    }
  };

  // Integration Functions
  const handleCreateIntegration = async (data) => {
    try {
      await axiosInstance.post('/admin/integrations', data);
      toast.success('Integration created');
      fetchIntegrations();
      setIntegrationDialog({ open: false, mode: 'create', data: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create integration');
    }
  };

  const handleUpdateIntegration = async (id, data) => {
    try {
      await axiosInstance.put(`/admin/integrations/${id}`, data);
      toast.success('Integration updated');
      fetchIntegrations();
      setIntegrationDialog({ open: false, mode: 'create', data: null });
    } catch (error) {
      toast.error('Failed to update integration');
    }
  };

  const handleDeleteIntegration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;
    try {
      await axiosInstance.delete(`/admin/integrations/${id}`);
      toast.success('Integration deleted');
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const handleTestIntegration = async (id) => {
    try {
      const res = await axiosInstance.post(`/admin/integrations/${id}/test`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
      fetchIntegrations();
    } catch (error) {
      toast.error('Test failed');
    }
  };

  const handleToggleIntegration = async (id, enabled) => {
    try {
      await axiosInstance.put(`/admin/integrations/${id}`, { enabled });
      toast.success(`Integration ${enabled ? 'enabled' : 'disabled'}`);
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to toggle integration');
    }
  };

  // Notification Functions
  const handleMarkRead = async (id) => {
    try {
      await axiosInstance.patch(`/admin/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axiosInstance.patch('/admin/notifications/read-all');
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleUpdatePreferences = async (prefs) => {
    try {
      await axiosInstance.put('/admin/notifications/preferences', prefs);
      toast.success('Preferences updated');
      setNotificationPrefs(prefs);
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };


  const getIntegrationIcon = (type) => {
    const icons = {
      slack: MessageSquare,
      email: Mail,
      teams: Users,
      teams_bot: Users,
      qflow_api: Link2,
      webhook: Webhook,
    };
    const Icon = icons[type] || Plug;
    return <Icon size={20} />;
  };

  const getStatusBadge = (status) => {
    const variants = {
      connected: 'bg-green-100 text-green-800',
      disconnected: 'bg-gray-100 text-gray-800',
      error: 'bg-red-100 text-red-800',
    };
    return <Badge className={variants[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getActionIcon = (action) => {
    if (action.includes('create')) return <Plus size={14} className="text-green-500" />;
    if (action.includes('update')) return <Edit size={14} className="text-blue-500" />;
    if (action.includes('delete')) return <Trash2 size={14} className="text-red-500" />;
    if (action.includes('login')) return <User size={14} className="text-purple-500" />;
    return <Activity size={14} className="text-gray-500" />;
  };

  const durationLabel = (event) => {
    if (!event.login_at) return '-';
    const end = event.logout_at ? new Date(event.logout_at) : new Date();
    const minutes = Math.max(0, Math.floor((end - new Date(event.login_at)) / 60000));
    const days = Math.floor(minutes / 1440); const hours = Math.floor((minutes % 1440) / 60); const mins = minutes % 60;
    return [days && `${days}d`, hours && `${hours}h`, `${mins}m`].filter(Boolean).join(' ');
  };
  const taskStatusClass = { open: 'bg-slate-100 text-slate-700', in_review: 'bg-blue-100 text-blue-700', blocked: 'bg-red-100 text-red-700', done: 'bg-emerald-100 text-emerald-700' };
  const taskPriorityClass = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-700' };
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="animate-spin text-sky-600" size={40} />
        </div>
      </div>
    );
  }

  const settingsNavClass = 'qflow-nav-item flex h-10 w-full items-center justify-start gap-3 rounded-lg border-0 bg-transparent px-3 text-sm font-medium shadow-none data-[state=active]:shadow-none';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header: one line, details behind the info tip */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">
              {integrationsOnly ? 'Plugins' : 'Admin'}
            </h1>
            <InfoTip label={integrationsOnly ? 'About Plugins' : 'About Admin'}>
              {integrationsOnly ? 'Connect Qflow to the apps your organisation already uses: ask Qflow from Teams or another app, and send alerts to Slack, a Teams channel, email or a webhook.' : 'Everything the Tenant Admin runs for this organisation: who can join, what people can do, leaders, security, knowledge rules, Web Mode, Floor Space privacy, organisation alerts and tasks. Your own settings live in your profile.'}
            </InfoTip>
          </div>
          <Button onClick={fetchData} variant="outline" size="icon" aria-label="Refresh" title="Refresh" data-testid="system-refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Settings: grouped side menu (stacks above the content on small screens) */}
        <Tabs value={visibleTab} onValueChange={setActiveTab} orientation="vertical" className="flex flex-wrap items-start gap-6">
          {!integrationsOnly && <TabsList aria-label="Settings sections" className="flex h-auto w-full flex-[1_1_220px] flex-col items-stretch justify-start gap-0.5 rounded-xl border border-gray-200 bg-white p-2 lg:sticky lg:top-6 lg:max-w-[240px]">
            {canManageAppearance && <>
            <TabsTrigger value="overview" className={settingsNavClass} data-testid="org-nav-overview">
              <LayoutDashboard size={16} aria-hidden="true" />
              Overview
            </TabsTrigger>
            <p className="qflow-nav-group" aria-hidden="true">People</p>
            <TabsTrigger value="members" className={settingsNavClass} data-testid="org-nav-members">
              <Users size={16} aria-hidden="true" />
              Members and access
            </TabsTrigger>
            {canEditAbilities && <TabsTrigger value="abilities" className={settingsNavClass} data-testid="org-nav-abilities">
              <CheckCircle size={16} aria-hidden="true" />
              Abilities
            </TabsTrigger>}
            <TabsTrigger value="leadership" className={settingsNavClass} data-testid="org-nav-leadership">
              <Star size={16} aria-hidden="true" />
              Leadership roles
            </TabsTrigger>
            <p className="qflow-nav-group" aria-hidden="true">Security</p>
            <TabsTrigger value="user-login" className={settingsNavClass}>
              <LogIn size={16} aria-hidden="true" />
              Sign-in history
            </TabsTrigger>
            <TabsTrigger value="audit" className={settingsNavClass}>
              <Shield size={16} aria-hidden="true" />
              Audit log
            </TabsTrigger>
            </>}
            {canManageWebMode && <>
              <p className="qflow-nav-group" aria-hidden="true">Knowledge</p>
              <TabsTrigger value="knowledge-rules" className={settingsNavClass}>
                <FileText size={16} aria-hidden="true" />
                Knowledge rules
              </TabsTrigger>
              <TabsTrigger value="web-mode" className={settingsNavClass}>
                <Globe2 size={16} aria-hidden="true" />
                Web Mode
              </TabsTrigger>
            </>}
            {canManageSystemOperations && <>
            <TabsTrigger value="floor-space" className={settingsNavClass}>
              <MessageCircle size={16} aria-hidden="true" />
              Floor Space privacy
            </TabsTrigger>
            <p className="qflow-nav-group" aria-hidden="true">Organisation</p>
            <TabsTrigger value="general" className={settingsNavClass}>
              <SlidersHorizontal size={16} aria-hidden="true" />
              Answer style
            </TabsTrigger>
            <TabsTrigger value="notifications" className={settingsNavClass}>
              <Bell size={16} aria-hidden="true" />
              Organisation alerts
              {notifications.unread_count > 0 && (
                <Badge variant="destructive" className="ml-auto text-xs">
                  {notifications.unread_count}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks" className={settingsNavClass}>
              <ClipboardList size={16} aria-hidden="true" />
              Tasks
            </TabsTrigger>
            </>}
          </TabsList>}
          <div className="min-w-0 flex-[999_1_560px] [&>[role=tabpanel]]:mt-0">

          {canManageAppearance && (
            <TabsContent value="general">
              <GeneralSettingsPanel
                showResponseTone={canManageQflow}
                showTheme={false}
              />
            </TabsContent>
          )}

          {canManageWebMode && (
            <TabsContent value="web-mode">
              <WebModeSettings />
            </TabsContent>
          )}

          {canManageWebMode && (
            <TabsContent value="knowledge-rules">
              <KnowledgeUploadSettings />
            </TabsContent>
          )}

          {canManageAppearance && <>
            <TabsContent value="overview"><OrganisationOverview onOpenSection={setActiveTab} canEditAbilities={canEditAbilities} /></TabsContent>
            <TabsContent value="members">
              <OrganizationMfaPolicy />
              {isTenantAdmin(user) && <OrganizationSessionPolicy />}
              {isTenantAdmin(user) && <OrganizationSsoSettings />}
              <div className="space-y-6">
                {user?.role === 'admin' && user?.is_master_admin !== true && <TenantAccessControls />}
                <DomainSettings />
              </div>
            </TabsContent>
            <TabsContent value="leadership"><LeadershipSection /></TabsContent>
            {canEditAbilities && <TabsContent value="abilities"><AbilitiesSettings /></TabsContent>}
          </>}

          <TabsContent value="user-login">
            <Card className="overflow-hidden border-slate-200 bg-white">
              <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4">
                <label className="min-w-[170px] text-xs font-medium text-slate-600">User<select value={loginFilters.user_id} onChange={(e) => setLoginFilters({...loginFilters,user_id:e.target.value})} className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"><option value="all">All users</option>{users.map((u)=><option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
                <label className="min-w-[150px] text-xs font-medium text-slate-600">Successful<select value={loginFilters.successful} onChange={(e)=>setLoginFilters({...loginFilters,successful:e.target.value})} className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"><option value="all">All</option><option value="success">Success only</option><option value="failed">Failed only</option></select></label>
                <label className="text-xs font-medium text-slate-600">Login from<input type="date" value={loginFilters.login_from} onChange={(e)=>setLoginFilters({...loginFilters,login_from:e.target.value})} className="mt-1 block h-10 rounded-lg border border-slate-200 px-3 text-sm" /></label>
                <label className="text-xs font-medium text-slate-600">Login to<input type="date" value={loginFilters.login_to} onChange={(e)=>setLoginFilters({...loginFilters,login_to:e.target.value})} className="mt-1 block h-10 rounded-lg border border-slate-200 px-3 text-sm" /></label>
                <label className="text-xs font-medium text-slate-600">Limiter<select value={loginFilters.limit} onChange={(e)=>setLoginFilters({...loginFilters,limit:e.target.value})} className="mt-1 block h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"><option>25</option><option>50</option><option>100</option><option>250</option></select></label>
              </div>
              <div className="overflow-x-auto"><table className="w-full min-w-[1150px] text-left text-sm"><thead className="bg-slate-100 text-slate-600"><tr>{['Username','Host','Host address','Login','Logout','Duration','Expired','Client version','Success'].map((h)=><th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead><tbody>{loginEvents.map((event,index)=>{const expired=event.expires_at&&new Date(event.expires_at)<new Date();return <tr key={event.id} className={`${index%2?'bg-slate-50':'bg-white'} hover:bg-blue-50`}><td className="px-4 py-3">{event.username}</td><td className="px-4 py-3">{event.host||'-'}</td><td className="px-4 py-3">{event.host_address||'-'}</td><td className="px-4 py-3">{event.login_at?format(new Date(event.login_at),'dd MMM yyyy HH:mm'):'-'}</td><td className="px-4 py-3">{event.logout_at?format(new Date(event.logout_at),'dd MMM yyyy HH:mm'):'-'}</td><td className="px-4 py-3">{durationLabel(event)}</td><td className="px-4 py-3">{expired?<Check size={16} className="text-blue-600"/>:'-'}</td><td className="max-w-[240px] truncate px-4 py-3" title={event.client_version}>{event.client_version||'-'}</td><td className="px-4 py-3">{event.success?<CheckCircle size={17} className="text-emerald-600"/>:<XCircle size={17} className="text-red-600"/>}</td></tr>})}{!loginEvents.length&&<tr><td colSpan="9" className="px-4 py-10 text-center text-slate-400">No login events match these filters. History begins after this update is deployed.</td></tr>}</tbody></table></div>
            </Card>
          </TabsContent>
          <TabsContent value="tasks">
            <Card className="overflow-hidden border-slate-200 bg-white">
              <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4"><label className="text-xs font-medium text-slate-600">Status<select value={taskFilters.status} onChange={(e)=>setTaskFilters({...taskFilters,status:e.target.value})} className="mt-1 block h-10 rounded-lg border border-slate-200 bg-white px-3"><option value="all">All</option><option value="open">Open</option><option value="in_review">In review</option><option value="blocked">Blocked</option><option value="done">Done</option></select></label><label className="min-w-[180px] text-xs font-medium text-slate-600">Assignee<select value={taskFilters.assignee_id} onChange={(e)=>setTaskFilters({...taskFilters,assignee_id:e.target.value})} className="mt-1 block h-10 w-full rounded-lg border border-slate-200 bg-white px-3"><option value="all">All assignees</option>{users.map((u)=><option key={u.id} value={u.id}>{u.name}</option>)}</select></label><label className="text-xs font-medium text-slate-600">Priority<select value={taskFilters.priority} onChange={(e)=>setTaskFilters({...taskFilters,priority:e.target.value})} className="mt-1 block h-10 rounded-lg border border-slate-200 bg-white px-3"><option value="all">All</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></label><Button className="ml-auto bg-blue-600 hover:bg-blue-700" onClick={()=>setTaskDialogOpen(true)}><Plus size={16} className="mr-2"/>New task</Button></div>
              <div className="overflow-x-auto"><table className="w-full min-w-[760px] text-left text-sm"><thead className="bg-slate-100 text-slate-600"><tr>{['Task','Assignee','Status','Priority','Due'].map((h)=><th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead><tbody>{tasks.map((task,index)=><tr key={task.id} className={`${index%2?'bg-slate-50':'bg-white'} hover:bg-blue-50`}><td className="px-4 py-3 font-medium">{task.title}</td><td className="px-4 py-3">{task.assignee_name||'Unassigned'}</td><td className="px-4 py-3"><select value={task.status} onChange={(e)=>updateTaskStatus(task.id,e.target.value)} className={`rounded-full border-0 px-2 py-1 text-xs font-medium ${taskStatusClass[task.status]}`}><option value="open">Open</option><option value="in_review">In review</option><option value="blocked">Blocked</option><option value="done">Done</option></select></td><td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-medium ${taskPriorityClass[task.priority]}`}>{task.priority}</span></td><td className="px-4 py-3">{task.due_date?format(new Date(`${task.due_date}T00:00:00`),'dd MMM yyyy'):'-'}</td></tr>)}{!tasks.length&&<tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No standalone tasks match these filters.</td></tr>}</tbody></table></div>
            </Card>
          </TabsContent>
          <TabsContent value="floor-space">
            <Card className="p-6 max-w-3xl">
              <div className="mb-5 flex items-center gap-2"><h2 className="text-lg font-bold text-gray-900">Floor Space Access & Privacy</h2><InfoTip label="About Floor Space access">Set the default audience and the choices available before a question is posted.</InfoTip></div>
              {floorSettings && <div className="space-y-5">
                <div><Label className="mb-2 block">Default visibility</Label><Select value={floorSettings.default_visibility} onValueChange={(value) => setFloorSettings({ ...floorSettings, default_visibility: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="organisation">Organisation-wide</SelectItem><SelectItem value="team">Team only (recommended)</SelectItem><SelectItem value="selected_people">Selected people</SelectItem><SelectItem value="private">Private</SelectItem></SelectContent></Select></div>
                {[
                  ['users_can_change_visibility', 'Users may change visibility', 'Allow the audience selector before posting'],
                  ['organisation_wide_posting', 'Organisation-wide posting', 'Allow questions visible across the organisation'],
                  ['selected_people_posting', 'Selected people', 'Allow named-user conversations'],
                  ['private_posting', 'Private questions', 'Allow author-only conversations with authorised private viewers'],
                ].map(([key, label, description]) => <div key={key} className="flex items-center justify-between gap-3 border-b border-gray-100 py-3 last:border-b-0"><div className="flex items-center gap-2"><Label htmlFor={`floor-${key}`}>{label}</Label><InfoTip label={`About ${label}`}>{description}</InfoTip></div><Switch id={`floor-${key}`} checked={floorSettings[key]} onCheckedChange={(checked) => setFloorSettings({ ...floorSettings, [key]: checked })} /></div>)}
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">Private conversations are not visible to ordinary administrators. Assign <code>floor_space_view_private</code> only to specifically authorised users.</div>
                <Button onClick={saveFloorSettings} disabled={!floorSettings.can_manage}>Save privacy settings</Button>
                {!floorSettings.can_manage && <p className="text-xs text-rose-600">You need the Floor Space settings permission to make changes.</p>}
              </div>}
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <Card className="p-6">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-gray-500 mb-1 block">Search Action</Label>
                  <Select
                    value={auditFilters.action}
                    onValueChange={(v) => setAuditFilters({ ...auditFilters, action: v === '__all__' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Actions</SelectItem>
                      {auditLogs.filters?.action_types?.map((action) => (
                        <SelectItem key={action} value={action}>
                          {action.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-gray-500 mb-1 block">Entity Type</Label>
                  <Select
                    value={auditFilters.entity_type}
                    onValueChange={(v) => setAuditFilters({ ...auditFilters, entity_type: v === '__all__' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Entities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Entities</SelectItem>
                      {auditLogs.filters?.entity_types?.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs text-gray-500 mb-1 block">Start Date</Label>
                  <Input
                    type="date"
                    value={auditFilters.start_date}
                    onChange={(e) => setAuditFilters({ ...auditFilters, start_date: e.target.value })}
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs text-gray-500 mb-1 block">End Date</Label>
                  <Input
                    type="date"
                    value={auditFilters.end_date}
                    onChange={(e) => setAuditFilters({ ...auditFilters, end_date: e.target.value })}
                  />
                </div>
                <div className="flex gap-2 pt-5">
                  <Button variant="outline" onClick={() => handleExportLogs('csv')} className="gap-2 rounded-lg border-0 text-slate-500 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-none">
                    <Download size={16} />
                    CSV
                  </Button>
                  <Button variant="outline" onClick={() => handleExportLogs('json')} className="gap-2 rounded-lg border-0 text-slate-500 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-none">
                    <Download size={16} />
                    JSON
                  </Button>
                </div>
              </div>

              {/* Logs Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Entity</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {auditLogs.logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.user_name || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className="text-sm">{log.action?.replace(/_/g, ' ')}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">
                            {log.entity_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {log.details ? JSON.stringify(log.details).slice(0, 50) + '...' : '-'}
                        </td>
                      </tr>
                    ))}
                    {auditLogs.logs.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                          <FileText size={40} className="mx-auto mb-2 text-gray-300" />
                          <p>No audit logs found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {auditPage * 50 + 1} - {Math.min((auditPage + 1) * 50, auditLogs.total)} of {auditLogs.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={auditPage === 0}
                    onClick={() => setAuditPage(auditPage - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(auditPage + 1) * 50 >= auditLogs.total}
                    onClick={() => setAuditPage(auditPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          {integrationsOnly && <TabsContent value="integrations">
            <PluginsPanel
              integrations={integrations}
              templates={integrationTemplates}
              alertEvents={alertEvents}
              iconFor={getIntegrationIcon}
              onAdd={(type, name) => setIntegrationDialog({ open: true, mode: 'create', data: { type, name } })}
              onEdit={(integration) => setIntegrationDialog({ open: true, mode: 'edit', data: integration })}
              onTest={handleTestIntegration}
              onToggle={handleToggleIntegration}
              onDelete={handleDeleteIntegration}
              onManage={setSelectedIntegration}
              onSetup={setTeamsSetupFor}
            />
            <div className="mt-5"><PluginsKnowledgeSources canConnect={isAdminOrPlatform(user)} /></div>
          </TabsContent>}

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Notifications List */}
              <Card className="p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">
                    Notifications
                    {notifications.unread_count > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {notifications.unread_count} unread
                      </Badge>
                    )}
                  </h3>
                  {notifications.unread_count > 0 && (
                    <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
                      Mark all read
                    </Button>
                  )}
                </div>

                <div className="space-y-3">
                  {notifications.notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                        notif.read ? 'bg-gray-50 border-gray-200' : 'bg-sky-50 border-sky-200'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          notif.read ? 'bg-gray-200' : 'bg-sky-200'
                        }`}
                      >
                        <Bell size={18} className={notif.read ? 'text-gray-500' : 'text-sky-600'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${notif.read ? 'text-gray-700' : 'text-gray-900'}`}>{notif.title}</p>
                        <p className="text-sm text-gray-500">{notif.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}
                        </p>
                      </div>
                      {!notif.read && (
                        <Button size="sm" variant="ghost" onClick={() => handleMarkRead(notif.id)}>
                          <CheckCircle size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                  {notifications.notifications.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <BellOff size={48} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-lg font-medium">No notifications</p>
                      <p className="text-sm">You're all caught up!</p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Activity Feed & Preferences */}
              <div className="space-y-6">
                {/* Organization Notification Preferences */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold">Organization notification rules</h3>
                  <p className="text-sm text-gray-500 mb-4">Controls notification delivery for this organization, including Review Queue assignments.</p>
                  {notificationPrefs && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm">Query Assignment Alerts</Label>
                          <p className="text-xs text-gray-500">Notify users when a Review Queue item is assigned or reassigned to them.</p>
                        </div>
                        <Switch
                          checked={notificationPrefs.query_assignment_alerts !== false}
                          onCheckedChange={(checked) =>
                            handleUpdatePreferences({ ...notificationPrefs, query_assignment_alerts: checked })
                          }
                        />
                      </div>
                      <div className="pt-2">
                        <Label className="text-sm text-gray-500 mb-2 block">Assignment Delivery</Label>
                        <Select
                          value={notificationPrefs.digest_frequency}
                          onValueChange={(v) => handleUpdatePreferences({ ...notificationPrefs, digest_frequency: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="realtime">Real-time</SelectItem>
                            <SelectItem value="daily">Daily Digest</SelectItem>
                            <SelectItem value="weekly">Weekly Digest</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </Card>

                {/* Activity Feed */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Activity Feed</h3>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {activityFeed.slice(0, 10).map((activity) => (
                      <div key={activity.id} className="flex items-start gap-3 text-sm">
                        {getActionIcon(activity.action)}
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-700">
                            <span className="font-medium">{activity.user_name}</span> {activity.description}
                          </p>
                          <p className="text-xs text-gray-400">
                            {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                    {activityFeed.length === 0 && (
                      <p className="text-center text-gray-500 py-4">No recent activity</p>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
          </div>
        </Tabs>

        {/* Integration Dialog */}
        <Dialog
          open={integrationDialog.open}
          onOpenChange={(open) => setIntegrationDialog({ ...integrationDialog, open })}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {integrationDialog.mode === 'edit' ? 'Edit plugin' : 'Add plugin'}
              </DialogTitle>
              <DialogDescription>
                Fill in the connection details, then choose which alerts it should send.
              </DialogDescription>
            </DialogHeader>
            <IntegrationForm
              mode={integrationDialog.mode}
              data={integrationDialog.data}
              templates={integrationTemplates}
              alertEvents={alertEvents}
              onSubmit={
                integrationDialog.mode === 'edit'
                  ? (data) => handleUpdateIntegration(integrationDialog.data.id, data)
                  : handleCreateIntegration
              }
              onCancel={() => setIntegrationDialog({ open: false, mode: 'create', data: null })}
            />
          </DialogContent>
        </Dialog>
        {teamsSetupFor && (
          <TeamsSetupDialog
            integration={teamsSetupFor}
            onClose={() => setTeamsSetupFor(null)}
            onManageLinks={(integration) => { setTeamsSetupFor(null); setSelectedIntegration(integration); }}
            onChanged={fetchIntegrations}
          />
        )}
        {selectedIntegration && (
          <IntegrationAccessManager
            integration={selectedIntegration}
            capabilities={integrationCapabilities}
            users={users}
            currentUser={user}
            onClose={() => setSelectedIntegration(null)}
          />
        )}
        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>New task</DialogTitle><DialogDescription>Create a standalone System task. This is not connected to Review Queue.</DialogDescription></DialogHeader>
            <form onSubmit={createTask} className="space-y-4"><label className="block text-sm font-medium">Title<Input className="mt-1" value={taskForm.title} onChange={(e)=>setTaskForm({...taskForm,title:e.target.value})} required /></label><label className="block text-sm font-medium">Assignee<select className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3" value={taskForm.assignee_id} onChange={(e)=>setTaskForm({...taskForm,assignee_id:e.target.value})}><option value="none">Unassigned</option>{users.map((u)=><option key={u.id} value={u.id}>{u.name}</option>)}</select></label><div className="grid grid-cols-2 gap-3"><label className="text-sm font-medium">Priority<select className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3" value={taskForm.priority} onChange={(e)=>setTaskForm({...taskForm,priority:e.target.value})}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></label><label className="text-sm font-medium">Status<select className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3" value={taskForm.status} onChange={(e)=>setTaskForm({...taskForm,status:e.target.value})}><option value="open">Open</option><option value="in_review">In review</option><option value="blocked">Blocked</option><option value="done">Done</option></select></label></div><label className="block text-sm font-medium">Due date<input type="date" className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3" value={taskForm.due_date} onChange={(e)=>setTaskForm({...taskForm,due_date:e.target.value})}/></label><DialogFooter><Button type="button" variant="outline" onClick={()=>setTaskDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create task</Button></DialogFooter></form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Integration Form Component
const IntegrationForm = ({ mode, data, templates, alertEvents = [], onSubmit, onCancel }) => {
  // Saved secrets arrive masked from the server. They start empty here; leaving one
  // empty keeps the stored value, and typing a new value replaces it.
  const savedSecrets = new Set(data?.secret_fields || []);
  const savedMasks = data?.config || {};
  const [formData, setFormData] = useState({
    name: data?.name || '',
    type: data?.type || 'slack',
    config: Object.fromEntries(Object.entries(data?.config || {}).filter(([key]) => !savedSecrets.has(key))),
    enabled: data?.enabled || false,
    alert_events: data?.alert_events || [],
  });
  const [showSecrets, setShowSecrets] = useState({});

  const template = templates[formData.type] || {};
  const configFields = template.config_fields || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    const config = Object.fromEntries(Object.entries(formData.config).filter(([key, value]) => !(savedSecrets.has(key) && !value)));
    onSubmit({ ...formData, config });
  };
  const secretHint = (key) => (savedSecrets.has(key) ? `Saved (${savedMasks[key]}). Leave blank to keep it.` : '');

  const toggleSecret = (key) => {
    setShowSecrets({ ...showSecrets, [key]: !showSecrets[key] });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Integration Type</Label>
        <Select
          value={formData.type}
          onValueChange={(v) => setFormData({ ...formData, type: v, config: {} })}
          disabled={mode === 'edit'}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(templates).map(([key, tmpl]) => (
              <SelectItem key={key} value={key}>
                {tmpl.icon} {tmpl.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Name</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="My Slack Integration"
          required
        />
      </div>

      {configFields.map((field) => (
        <div key={field.key}>
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.type === 'select' ? (
            <Select
              value={formData.config[field.key] || field.default || ''}
              onValueChange={(v) => setFormData({ ...formData, config: { ...formData.config, [field.key]: v } })}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Select ${field.label}`} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : field.type === 'textarea' ? (
            <Textarea
              value={formData.config[field.key] || ''}
              onChange={(e) => setFormData({ ...formData, config: { ...formData.config, [field.key]: e.target.value } })}
              placeholder={secretHint(field.key) || field.default || ''}
            />
          ) : field.type === 'password' ? (
            <div className="relative">
              <Input
                type={showSecrets[field.key] ? 'text' : 'password'}
                value={formData.config[field.key] || ''}
                onChange={(e) =>
                  setFormData({ ...formData, config: { ...formData.config, [field.key]: e.target.value } })
                }
                placeholder={secretHint(field.key) || '••••••••'}
                required={field.required && !savedSecrets.has(field.key)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => toggleSecret(field.key)}
              >
                {showSecrets[field.key] ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          ) : (
            <Input
              type={field.type || 'text'}
              value={formData.config[field.key] || ''}
              onChange={(e) => setFormData({ ...formData, config: { ...formData.config, [field.key]: e.target.value } })}
              placeholder={secretHint(field.key) || field.default?.toString() || ''}
              required={field.required && !savedSecrets.has(field.key)}
            />
          )}
        </div>
      ))}

      {template.supports_alerts && alertEvents.length > 0 && <fieldset className="space-y-2 rounded-lg border border-gray-200 p-3" data-testid="plugin-alert-events">
        <legend className="px-1 text-sm font-medium text-gray-700">Send these alerts</legend>
        {alertEvents.map((event) => <label key={event.key} className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="h-4 w-4 accent-sky-600" checked={formData.alert_events.includes(event.key)}
            onChange={(e) => setFormData({ ...formData, alert_events: e.target.checked ? [...formData.alert_events, event.key] : formData.alert_events.filter((key) => key !== event.key) })} />
          {event.label}
        </label>)}
      </fieldset>}

      <div className="flex items-center gap-2">
        <Switch checked={formData.enabled} onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })} />
        <Label>Turn on</Label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">{mode === 'edit' ? 'Save plugin' : 'Add plugin'}</Button>
      </DialogFooter>
    </form>
  );
};

export default System;
